import React, { useState, useRef, useEffect } from "react";
import { IconButton } from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ImageCarouselModal from "./ImageCarouselModal";

interface UploadedImagePreviewsProps {
  previewUrls: any[];
  handleRemovePhoto?: (index: number) => void;
  showRemoveButton?: boolean;
  hideHeader?: boolean;
}

const UploadedImagePreviews: React.FC<UploadedImagePreviewsProps> = ({
  previewUrls,
  handleRemovePhoto,
  showRemoveButton = false,
  hideHeader = false,
}) => {
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [showArrows, setShowArrows] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const checkOverflow = () => {
    if (scrollContainerRef.current) {
      const { scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowArrows(scrollWidth > clientWidth);
    }
  };

  useEffect(() => {
    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [previewUrls]);

  // Give a small delay after render to re-measure in case fonts/images push layout
  useEffect(() => {
    const timer = setTimeout(checkOverflow, 100);
    return () => clearTimeout(timer);
  });

  if (!previewUrls || previewUrls.length === 0) return null;

  const handleImageClick = (index: number) => {
    setCarouselIndex(index);
    setCarouselOpen(true);
  };

  const scroll = (offset: number) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: offset, behavior: "smooth" });
    }
  };

  return (
    <div style={{ marginBottom: hideHeader ? "0" : "2rem" }}>
      <style>{`
        .hide-scroll::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      
      {!hideHeader && (
        <h4
          style={{
            margin: "0 0 1rem 0",
            color: "#999",
            fontSize: "0.85rem",
            textTransform: "uppercase",
            letterSpacing: "1px",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          {previewUrls.length} {previewUrls.length === 1 ? "PHOTO" : "PHOTOS"} UPLOADED
        </h4>
      )}
      
      <div style={{ display: "flex", alignItems: "center", width: "100%", gap: "12px" }}>
        {showArrows && (
          <button
            onClick={() => scroll(-200)}
            style={{
              zIndex: 2,
              backgroundColor: "#fff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              border: "none",
              borderRadius: "50%",
              color: "#d92b3c",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "28px",
              height: "28px",
              padding: 0,
              flexShrink: 0
            }}
          >
            <ChevronLeftIcon fontSize="small" />
          </button>
        )}

        <div
          className="hide-scroll"
          ref={scrollContainerRef}
          style={{ 
            display: "flex", 
            overflowX: "auto", 
            gap: "1rem", 
            scrollbarWidth: "none", 
            msOverflowStyle: "none", 
            padding: "0.5rem",
            margin: "-0.5rem", // offset padding to keep alignment
            flex: 1 
          }}
        >
          {previewUrls.map((item, index) => {
            const displayUrl = item.tempThumbFileUrl || item.url;
            return (
              <div
                key={index}
                onClick={() => handleImageClick(index)}
                style={{
                  position: "relative",
                  flexShrink: 0,
                  width: "80px",
                  height: "80px",
                  borderRadius: "8px",
                  border: "2px solid #fff",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  overflow: "hidden",
                  cursor: "pointer",
                  background: "#fff",
                }}
              >
                {typeof displayUrl === "string" &&
                (displayUrl.startsWith("http") ||
                  displayUrl.startsWith("data:image/") ||
                  displayUrl.startsWith("blob:")) ? (
                  <>
                    <img
                      src={displayUrl}
                      alt=""
                      aria-hidden="true"
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        filter: "blur(8px) brightness(0.7)",
                        transform: "scale(1.2)",
                      }}
                    />
                    <img
                      src={displayUrl}
                      alt={item.file?.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        position: "relative",
                      }}
                    />
                  </>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      background: "#fff",
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      height="24"
                      width="24"
                      fill="#d92b3c"
                      viewBox="0 0 24 24"
                    >
                      <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
                    </svg>
                  </div>
                )}

                {showRemoveButton && handleRemovePhoto && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemovePhoto(index);
                    }}
                    style={{
                      position: "absolute",
                      top: "4px",
                      right: "4px",
                      background: "rgba(0,0,0,0.6)",
                      color: "white",
                      border: "none",
                      borderRadius: "50%",
                      width: "20px",
                      height: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      padding: 0,
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      height="12"
                      width="12"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                    </svg>
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {showArrows && (
          <button
            onClick={() => scroll(200)}
            style={{
              zIndex: 2,
              backgroundColor: "#fff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              border: "none",
              borderRadius: "50%",
              color: "#d92b3c",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "28px",
              height: "28px",
              padding: 0,
              flexShrink: 0
            }}
          >
            <ChevronRightIcon fontSize="small" />
          </button>
        )}
      </div>

      <ImageCarouselModal
        isOpen={carouselOpen}
        onClose={() => setCarouselOpen(false)}
        images={previewUrls}
        initialIndex={carouselIndex}
      />
    </div>
  );
};

export default UploadedImagePreviews;
